import { useCallback, useEffect, useState } from 'react'
import { useUserState } from '~/atoms/UserState'
import { GeneralOperation } from '~/hooks/toast/eventHandlers/generalToastEventHandler'
import { useAnalytics } from '~/hooks/useAnalytics'
import { useEmitterService } from '~/hooks/useEmitterService'
import {
  NotificationDisposition,
  NotificationDomain,
  NotificationEmitterEventType,
} from '~/services/EventEmitter/types/NotificationEmitterTypes'
import Logger from '~/services/Logger'
import {
  AnalyticsNamespace,
  AuthenticationAnalyticsEvents,
} from '~/types/Analytics'
import { addRecentPbx } from '~/utils/recentPbxs'

import type { Pbx } from '@mangovoice/types'

export type PbxSummary = Pick<Pbx, 'id' | 'name'>

const logger = new Logger('usePbxSwitchExecutor')

// How long we'll keep the pending-switch flag alive before giving up.
// If the new PBX's session dependencies haven't loaded in this window we
// assume the switch silently failed and drop the flag so a later
// navigation to the same PBX doesn't surface a stale success toast.
const EXPECTED_PBX_TIMEOUT_MS = 30_000

export const usePbxSwitchExecutor = () => {
  const { track } = useAnalytics(AnalyticsNamespace.AUTHENTICATION)
  const { pbx, getUserSessionDependencies, isErrored } = useUserState()
  const { emit } = useEmitterService()
  const [expectedPbxId, setExpectedPbxId] = useState<number | undefined>()

  useEffect(() => {
    if (!expectedPbxId) return
    if (expectedPbxId === pbx?.id) {
      emit(NotificationEmitterEventType.PROCESS_UPDATED, {
        disposition: NotificationDisposition.Success,
        domain: NotificationDomain.GENERAL,
        operation: GeneralOperation.SwitchedPbx,
        message: pbx?.name,
      })
      setExpectedPbxId(undefined)
    }
  }, [expectedPbxId, pbx, emit])

  useEffect(() => {
    if (expectedPbxId && isErrored) {
      setExpectedPbxId(undefined)
    }
  }, [expectedPbxId, isErrored])

  // Safety net: if neither the success path (PBX id matches) nor the error
  // path (`isErrored` flips true) clears the pending flag within the
  // timeout, drop it ourselves so we don't fire a stale success toast on a
  // future navigation to the same PBX.
  useEffect(() => {
    if (!expectedPbxId) return
    const timeoutId = window.setTimeout(() => {
      logger.warn(
        `Timed out waiting for pbx ${expectedPbxId} to load; clearing pending switch state`,
      )
      setExpectedPbxId(undefined)
    }, EXPECTED_PBX_TIMEOUT_MS)
    return () => window.clearTimeout(timeoutId)
  }, [expectedPbxId])

  const executePbxSwitch = useCallback(
    (selectedPbx: PbxSummary) => {
      logger.info('Confirmed switch to pbx, switching now...')
      track(AuthenticationAnalyticsEvents.PbxSwitched)
      addRecentPbx(selectedPbx)
      setExpectedPbxId(selectedPbx.id)
      getUserSessionDependencies(selectedPbx)
    },
    [getUserSessionDependencies, track],
  )

  return {
    currentPbx: pbx ? { id: pbx.id, name: pbx.name } : undefined,
    executePbxSwitch,
  }
}
